import { type FirebaseApp, getApp, getApps, initializeApp } from "firebase/app";
import { type Analytics, isSupported, logEvent } from "firebase/analytics";
import { hasAnalyticsConsent } from "./ConsentProvider";

// Firebase web config is not a secret; it identifies the project the same way a
// domain name does. Access control is enforced by Firebase Security Rules and
// the Google Cloud API key restrictions on this key, not by hiding this object.
// This is the "mld" web app registered under the pearson-courseware-companion
// Firebase project. Values are read from env (see .env, which is committed
// since none of this is sensitive) rather than inlined here.
const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

export function getFirebaseApp(): FirebaseApp {
  return getApps().length ? getApp() : initializeApp(firebaseConfig);
}

let analyticsPromise: Promise<Analytics | null> | null = null;

/**
 * Lazily initializes Firebase Analytics on the client only. `isSupported()`
 * resolves false in browsers without IndexedDB/cookies (e.g. some in-app
 * webviews or when tracking is blocked), so callers must tolerate `null`.
 *
 * Also refuses to initialize until the visitor has granted analytics
 * consent, so the GA4 script and its cookies never load pre-consent, and
 * refuses if the env config is missing (e.g. a checkout without `.env`).
 */
export function getFirebaseAnalytics(): Promise<Analytics | null> {
  if (
    typeof window === "undefined" ||
    !hasAnalyticsConsent() ||
    !firebaseConfig.apiKey
  ) {
    return Promise.resolve(null);
  }

  if (!analyticsPromise) {
    analyticsPromise = isSupported().then((supported) => {
      if (!supported) return null;
      // Imported dynamically here (rather than at module scope) so the
      // analytics runtime is only pulled into the bundle path that needs it.
      return import("firebase/analytics").then(({ getAnalytics }) =>
        getAnalytics(getFirebaseApp())
      );
    });
  }

  return analyticsPromise;
}

export async function trackPageView(path: string) {
  const analytics = await getFirebaseAnalytics();
  if (!analytics) return;
  logEvent(analytics, "page_view", {
    page_path: path,
    page_location: typeof window !== "undefined" ? window.location.href : undefined,
  });
}

export async function trackEvent(eventName: string, params?: Record<string, unknown>) {
  const analytics = await getFirebaseAnalytics();
  if (!analytics) return;
  logEvent(analytics, eventName, params);
}
